import type { ApplicationDraft, ApplicationStep, ApplicationValidation } from "../api/client";

/**
 * The certification body application, client side.
 *
 * The whole six-step form is submitted as one payload, so the part-completed application
 * is client state until the last step. The server owns whether it is *valid*; this module
 * owns only what has been typed so far.
 */

/** One segment of the step rail. */
export interface StepDefinition {
  /**
   * The server answers this segment carries. Usually one; the Platform step carries two,
   * because screen 06 has two cards and they are different kinds of answer — nothing on
   * the brand card can block (ADR 0007) and three fields on the configuration card can.
   */
  keys: readonly ApplicationStep[];
  title: string;
  /** Screen number in the walkthrough, for orientation. */
  screen: string;
}

/** Six, as the prototype's rail and the specification both have it. */
export const STEPS: readonly StepDefinition[] = [
  { keys: ["legal_identity"], title: "Legal identity", screen: "01" },
  { keys: ["accreditation"], title: "Accreditation", screen: "02" },
  { keys: ["impartiality_and_liability"], title: "Impartiality", screen: "04" },
  { keys: ["people_and_roles"], title: "People", screen: "05" },
  { keys: ["brand", "platform"], title: "Platform", screen: "06" },
  { keys: ["agreements"], title: "Agreements", screen: "07" },
];

/** A step is done when every answer it carries is. */
export function stepComplete(
  validation: ApplicationValidation | null,
  step: StepDefinition | undefined,
): boolean {
  if (!validation || !step) return false;
  return step.keys.every((key) => resultFor(validation, key)?.complete === true);
}

/** Blocking findings across every answer the step carries, for the outstanding count. */
export function blockingCount(
  validation: ApplicationValidation | null,
  step: StepDefinition | undefined,
): number {
  if (!validation || !step) return 0;
  return step.keys.reduce(
    (total, key) => total + (resultFor(validation, key)?.blocking.length ?? 0),
    0,
  );
}

/**
 * Nothing pre-filled, and in particular no pre-selected Yes or No.
 *
 * `impartiality_committee` and `consultancy_offered` are `boolean not null` in the data
 * dictionary, and an unanswered question is not the same fact as a No. Defaulting either
 * would write down an answer the applicant never gave.
 */
export const EMPTY_APPLICATION: ApplicationDraft = {
  legal_name: "",
  trading_name: "",
  registration_number: "",
  country: "",
  address: "",
  accreditation_body: "",
  accreditation_number: "",
  accreditation_status: "",
  accreditation_expiry: "",
  next_assessment: "",
  mla_signatory: null,
  scopes: [],
  evidence: [],
  impartiality_committee: null,
  consultancy_offered: null,
  liability_cover: "",
  admin_contact: "",
  admin_email: "",
  decision_maker: "",
  decision_maker_email: "",
  technical_manager: "",
  billing_contact: "",
  brand_logo_key: null,
  brand_accent: "",
  accreditation_marks: false,
  reference_prefix: "",
  timezone: "",
  retention_policy: "",
  enabled_standards: [],
  duration_threshold: "",
  stage1_percentage: "",
  platform_terms: null,
  data_processing: null,
  authority_to_bind: null,
  authority_name: "",
};

/**
 * Which standards may be enabled: the ones the accredited scope actually carries.
 *
 * A standard ticked with no sector accredits nothing, so it is not offered here either —
 * the chip would promise something the gate refuses.
 */
export function enabledCandidates(draft: ApplicationDraft): string[] {
  return (draft.scopes ?? [])
    .filter((entry) => (entry.sectors ?? []).length > 0)
    .map((entry) => entry.standard);
}

export function isEnabled(draft: ApplicationDraft, standard: string): boolean {
  return (draft.enabled_standards ?? []).includes(standard);
}

/** Turn one standard on or off. A preference — never the accreditation. */
export function toggleEnabled(draft: ApplicationDraft, standard: string): ApplicationDraft {
  const enabled = draft.enabled_standards ?? [];
  return {
    ...draft,
    enabled_standards: enabled.includes(standard)
      ? enabled.filter((name) => name !== standard)
      : [...enabled, standard],
  };
}

/** The server's verdict on one step, or null while the first response is outstanding. */
export function resultFor(
  validation: ApplicationValidation | null,
  step: ApplicationStep | null,
) {
  if (!validation || !step) return null;
  return validation.steps.find((entry) => entry.step === step) ?? null;
}

/** The blocking finding against one field, if there is one. */
export function blockingFor(
  validation: ApplicationValidation | null,
  step: ApplicationStep | null,
  field: string,
): string | undefined {
  return resultFor(validation, step)?.blocking.find((issue) => issue.field === field)?.detail;
}

/** Sectors recorded against one standard, in the order the registry lists them. */
export function sectorsFor(draft: ApplicationDraft, standard: string): number[] {
  return draft.scopes?.find((entry) => entry.standard === standard)?.sectors ?? [];
}

export function hasStandard(draft: ApplicationDraft, standard: string): boolean {
  return (draft.scopes ?? []).some((entry) => entry.standard === standard);
}

/**
 * Add or remove a standard from the accredited scope.
 *
 * Removing a standard removes its sectors with it, and switches it off on the Platform
 * step. Keeping either would leave the application claiming to have enabled a standard it
 * no longer says it is accredited for — which the server refuses, correctly, but three
 * steps later and with no obvious cause.
 */
export function toggleStandard(draft: ApplicationDraft, standard: string): ApplicationDraft {
  const scopes = draft.scopes ?? [];
  if (!hasStandard(draft, standard)) {
    return { ...draft, scopes: [...scopes, { standard, sectors: [] }] };
  }
  return {
    ...draft,
    scopes: scopes.filter((entry) => entry.standard !== standard),
    enabled_standards: (draft.enabled_standards ?? []).filter((name) => name !== standard),
  };
}

/** Replace the whole sector list under one standard — the multi-select's bulk actions. */
export function setScopeSectors(
  draft: ApplicationDraft,
  standard: string,
  sectors: readonly number[],
): ApplicationDraft {
  return {
    ...draft,
    scopes: (draft.scopes ?? []).map((entry) =>
      entry.standard === standard
        ? { standard, sectors: [...sectors].sort((a, b) => a - b) }
        : entry,
    ),
  };
}

/** Add or remove one IAF sector under one standard. Sectors stay in ascending order. */
export function toggleSector(
  draft: ApplicationDraft,
  standard: string,
  sector: number,
): ApplicationDraft {
  return {
    ...draft,
    scopes: (draft.scopes ?? []).map((entry) => {
      if (entry.standard !== standard) return entry;
      const sectors = entry.sectors ?? [];
      return {
        standard,
        sectors: sectors.includes(sector)
          ? sectors.filter((index) => index !== sector)
          : [...sectors, sector].sort((a, b) => a - b),
      };
    }),
  };
}
